import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import liff from "@line/liff";

interface LiffContextType {
  liff: typeof liff | null;
  liffError: string | null;
  isLoggedIn: boolean;
  isInClient: boolean;
  isReady: boolean;
  idToken: string | null;
}

const LiffContext = createContext<LiffContextType | null>(null);

interface LiffProviderProps {
  children: ReactNode;
  liffId: string;
}

interface LiffState {
  liffObject: typeof liff | null;
  liffError: string | null;
  isLoggedIn: boolean;
  isInClient: boolean;
  isReady: boolean;
  idToken: string | null;
}

type LiffAction =
  | { type: 'INIT_SUCCESS'; liff: typeof liff; isLoggedIn: boolean; isInClient: boolean; idToken: string | null }
  | { type: 'INIT_ERROR'; error: string }
  | { type: 'SET_READY' };

const initialLiffState: LiffState = {
  liffObject: null,
  liffError: null,
  isLoggedIn: false,
  isInClient: false,
  isReady: false,
  idToken: null,
};

function liffReducer(state: LiffState, action: LiffAction): LiffState {
  switch (action.type) {
    case 'INIT_SUCCESS':
      return {
        ...state,
        liffObject: action.liff,
        isLoggedIn: action.isLoggedIn,
        isInClient: action.isInClient,
        idToken: action.idToken,
        isReady: true,
      };
    case 'INIT_ERROR':
      return { ...state, liffError: action.error, isReady: true };
    default:
      return state;
  }
}

export function LiffProvider({ children, liffId }: LiffProviderProps) {
  const [state, dispatch] = useReducer(liffReducer, initialLiffState);

  // Use ref to prevent double initialization in React Strict Mode
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) {
      return;
    }
    initRef.current = true;

    console.log("Initializing LIFF with ID:", liffId);

    // Initialize LIFF — do NOT use withLoginOnExternalBrowser because it
    // redirects to the Endpoint URL root, ignoring the current page path.
    // Instead we call liff.login() manually with redirectUri = current URL.
    liff
      .init({ liffId })
      .then(() => {
        console.log("LIFF init successful, isLoggedIn:", liff.isLoggedIn());

        // Handle LIFF deep-linking (Concatenate mode):
        // When liff.state is present, navigate to that path first.
        if (window.location.search) {
          const url = new URL(window.location.href);
          const liffState = url.searchParams.get("liff.state");

          if (liffState && liffState !== "/" && liffState !== window.location.pathname) {
            console.log("LIFF deep-link detected, navigating to:", liffState);
            window.location.replace(window.location.origin + liffState);
            return;
          }

          // Clean up LIFF/auth query params from the URL
          const liffParams = ["liff.state", "code", "state", "liffClientId", "liffRedirectUri", "bot_prompt"];
          let cleaned = false;
          for (const param of liffParams) {
            if (url.searchParams.has(param)) {
              url.searchParams.delete(param);
              cleaned = true;
            }
          }
          if (cleaned) {
            window.history.replaceState({}, "", url.pathname + (url.search || ""));
            console.log("Cleared LIFF params from URL");
          }
        }

        // Auto-login in external browser: redirect to LINE login with
        // redirectUri = current page, so user comes back to the same path.
        if (!liff.isLoggedIn() && !liff.isInClient()) {
          console.log("Not logged in (external browser), redirecting to LINE login...");
          liff.login({ redirectUri: window.location.href });
          return;
        }

        // Capture ID token immediately at init (it has a short TTL)
        let idToken: string | null = null;
        if (liff.isLoggedIn()) {
          let tokenFetchError = false;
          let fetchedToken: string | null = null;
          let fetchedAccessToken: string | null = null;
          try {
            fetchedToken = liff.getIDToken();
            fetchedAccessToken = liff.getAccessToken();
          } catch (e) {
            tokenFetchError = true;
            console.warn("Failed to get ID token at init:", e);
          }
          if (!tokenFetchError) {
            idToken = fetchedToken;
            const tokenPreview = fetchedToken ? fetchedToken.substring(0, 20) + "..." : "null";
            console.log("ID token captured at init:", tokenPreview);
            console.log("Access token available:", fetchedAccessToken !== null);
            if (!fetchedToken && fetchedAccessToken) {
              console.warn(
                'ID token is null but access token exists. ' +
                'Enable the "openid" scope in LINE Developers Console → LIFF app settings.'
              );
            }
          }
        }

        dispatch({
          type: 'INIT_SUCCESS',
          liff,
          isLoggedIn: liff.isLoggedIn(),
          isInClient: liff.isInClient(),
          idToken,
        });
      })
      .catch((error: Error) => {
        console.error("LIFF initialization failed:", error);
        dispatch({ type: 'INIT_ERROR', error: error.message });
      });
  }, [liffId]);

  return (
    <LiffContext.Provider
      value={{
        liff: state.liffObject,
        liffError: state.liffError,
        isLoggedIn: state.isLoggedIn,
        isInClient: state.isInClient,
        isReady: state.isReady,
        idToken: state.idToken,
      }}
    >
      {children}
    </LiffContext.Provider>
  );
}

export function useLiff() {
  const context = useContext(LiffContext);
  if (context === null) {
    throw new Error("useLiff must be used within a LiffProvider");
  }
  return context;
}
